import React, { useState, useEffect } from 'react';
import { Button, Form, Modal, InputGroup, Card, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { storageConfig } from '../config/authConfig';

const ContainerList: React.FC<{
  onSelectContainer: (container: string) => void;
  isAdmin: boolean;
}> = ({
  onSelectContainer,
  isAdmin
}) => {
    const { storageService, account } = useAuth();
    const [containers, setContainers] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [newContainerName, setNewContainerName] = useState<string>('');
    const [selectedContainer, setSelectedContainer] = useState<string | null>(null);
    const [showLinkModal, setShowLinkModal] = useState<boolean>(false);
    const [linkExpiryDays, setLinkExpiryDays] = useState<number>(7);
    const [generatingLink, setGeneratingLink] = useState<boolean>(false);
    const [generatedLink, setGeneratedLink] = useState<string>('');

    // Invite User State
    const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
    const [inviteEmail, setInviteEmail] = useState<string>('');
    const [invitePermissions, setInvitePermissions] = useState<'read' | 'write' | 'readwrite'>('read');
    const [inviting, setInviting] = useState<boolean>(false);
    const [inviteStatus, setInviteStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [invitedCredentials, setInvitedCredentials] = useState<{ email: string; password: string } | null>(null);
    const [copiedCreds, setCopiedCreds] = useState<boolean>(false);

    useEffect(() => {
      fetchContainers();
    }, [storageService]);

    const fetchContainers = async (): Promise<void> => {
      if (!storageService) return;

      setLoading(true);
      setError(null);

      try {
        const containerList = await storageService.getContainers();
        setContainers(containerList);
      } catch (err) {
        setError('Failed to fetch containers. Please try again.');
        console.error('Error fetching containers:', err);
      } finally {
        setLoading(false);
      }
    };

    const handleCreateContainer = async (): Promise<void> => {
      if (!storageService || !newContainerName.trim()) return;

      try {
        await storageService.createContainer(newContainerName.toLowerCase());
        setShowCreateModal(false);
        setNewContainerName('');
        fetchContainers();
      } catch (err) {
        setError('Failed to create container. Please try again.');
        console.error('Error creating container:', err);
      }
    };

    const handleDeleteContainer = async (containerName: string): Promise<void> => {
      if (!storageService) return;

      if (window.confirm(`Are you sure you want to delete the container "${containerName}"? This action cannot be undone.`)) {
        try {
          await storageService.deleteContainer(containerName);
          fetchContainers();
        } catch (err) {
          setError('Failed to delete container. Please try again.');
          console.error('Error deleting container:', err);
        }
      }
    };

    const handleContainerSelect = (containerName: string): void => {
      setSelectedContainer(containerName);
      onSelectContainer(containerName);
    };

    const openLinkModal = (containerName: string): void => {
      setSelectedContainer(containerName);
      setGeneratedLink('');
      setLinkExpiryDays(7);
      setShowLinkModal(true);
    };

    const generateUploadLink = async (): Promise<void> => {
      if (!storageService || !selectedContainer || !account) return;
      setGeneratingLink(true);
      try {
        const userObjectId = account.localAccountId || account.homeAccountId || '';
        const userEmail = account.username || '';

        const sas = await storageService.generateContainerUploadSas(
          selectedContainer,
          linkExpiryDays,
          userObjectId,
          userEmail
        );

        const shareUrl = `${window.location.origin}/upload?container=${encodeURIComponent(selectedContainer)}&sas=${encodeURIComponent(sas)}`;
        setGeneratedLink(shareUrl);
      } catch (err: any) {
        setError(err.message || 'Failed to generate upload link. Please try again.');
        console.error('Error generating upload link:', err);
      } finally {
        setGeneratingLink(false);
      }
    };

    const copyLink = async (): Promise<void> => {
      if (!generatedLink) return;
      try {
        await navigator.clipboard.writeText(generatedLink);
        alert('Link copied to clipboard');
      } catch (e) {
        console.error('Copy failed:', e);
      }
    };

    const handleOpenInviteModal = (containerName: string): void => {
      setSelectedContainer(containerName);
      setInviteEmail('');
      setInvitePermissions('read');
      setInviteStatus(null);
      setShowInviteModal(true);
    };

    const handleInviteUser = async (): Promise<void> => {
      if (!selectedContainer || !inviteEmail) return;

      setInviting(true);
      setInviteStatus(null);
      setInvitedCredentials(null);
      setCopiedCreds(false);

      try {
        const functionUrl = (process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api') + '/invite_user_to_container';

        const credentials = {
          userObjectId: account?.localAccountId || account?.homeAccountId || '',
          userEmail: account?.username || ''
        };

        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...credentials,
            email: inviteEmail,
            containerName: selectedContainer,
            permissions: invitePermissions
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to invite user');
        }

        const tempPw = data.temporaryPassword;
        if (tempPw) {
          setInvitedCredentials({ email: inviteEmail, password: tempPw });
        }
        setInviteStatus({
          type: 'success',
          message: data.message || `Access granted for ${inviteEmail}`
        });

        // Clear form after success (optional, keeping it open to invite more or see success msg)
        setInviteEmail('');

      } catch (err: any) {
        console.error('Invite error:', err);
        setInviteStatus({
          type: 'error',
          message: err.message || 'Failed to send invitation'
        });
      } finally {
        setInviting(false);
      }
    };

    if (loading && containers.length === 0) {
      return (
        <Card>
          <Card.Body className="text-center py-5">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-2 mb-0">Loading containers...</p>
          </Card.Body>
        </Card>
      );
    }

    return (
      <>
        <Card>
          <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
            <div>
              <i className="bi bi-folder2-open me-2"></i>
              Containers
            </div>
            {isAdmin && (
              <Button
                variant="light"
                size="sm"
                onClick={() => setShowCreateModal(true)}
              >
                <i className="bi bi-plus-lg"></i>
              </Button>
            )}
          </Card.Header>

          {error && (
            <div className="alert alert-danger m-3 mb-0" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          <Card.Body className="p-0">
            {containers.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-folder-x" style={{ fontSize: '4rem', color: '#ccc' }}></i>
                <h5 className="mt-3">No containers found</h5>
                {isAdmin && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-2"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    Create your first container
                  </Button>
                )}
              </div>
            ) : (
              <>
                {containers.map((container) => (
                  <div
                    key={container}
                    className={`p-3 border-bottom ${selectedContainer === container ? 'bg-light' : ''}`}
                    style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                    onClick={() => handleContainerSelect(container)}
                    onMouseEnter={(e) => {
                      if (selectedContainer !== container) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedContainer !== container) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          <i className="bi bi-folder me-2 text-primary"></i>
                          <strong>{container}</strong>
                        </div>
                        <small className="text-muted">Storage Container</small>
                      </div>
                      {isAdmin && (
                        <div
                          className="d-flex gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenInviteModal(container)}
                            title="Invite a user to this folder"
                          >
                            <i className="bi bi-person-plus me-1"></i>
                            Invite
                          </Button>
                          {/* Removed legacy link icon
                          <Button
                            variant="light"
                            size="sm"
                            className="btn-sm"
                            onClick={() => openLinkModal(container)}
                            title="Generate upload link"
                          >
                            <i className="bi bi-link-45deg"></i>
                          </Button>
                          */}
                          <Button
                            variant="light"
                            size="sm"
                            className="btn-sm text-danger"
                            onClick={() => handleDeleteContainer(container)}
                            title="Delete container"
                          >
                            <i className="bi bi-trash3"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </Card.Body>
        </Card>

        {/* Create Container Modal */}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Create New Container</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Container Name</Form.Label>
              <Form.Control
                type="text"
                value={newContainerName}
                onChange={(e) => setNewContainerName(e.target.value)}
                placeholder="Enter container name"
              />
              <Form.Text className="text-muted">
                Container names must be lowercase, between 3-63 characters, and can contain only letters, numbers, and dashes.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateContainer}
              disabled={!newContainerName.trim()}
            >
              Create
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Generate Upload Link Modal */}
        <Modal show={showLinkModal} onHide={() => setShowLinkModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Generate Upload Link</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Expiry (days)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={linkExpiryDays}
                onChange={(e) => setLinkExpiryDays(Number(e.target.value))}
              />
              <Form.Text className="text-muted">
                Link allows anyone with URL to upload to container.
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Sharable Link</Form.Label>
              <InputGroup>
                <Form.Control type="text" value={generatedLink} readOnly placeholder="Click Generate to create link" />
                <Button variant="outline-secondary" onClick={copyLink} disabled={!generatedLink}>Copy</Button>
              </InputGroup>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLinkModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={generateUploadLink} disabled={generatingLink || !selectedContainer}>
              {generatingLink ? 'Generating…' : 'Generate'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Invite User Modal */}
        <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Invite User to Container</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Invite a user to <strong>{selectedContainer}</strong>. If they don't have an account, they will receive an email invitation.
            </p>

            {inviteStatus && (
              <Alert variant={inviteStatus.type === 'success' ? 'success' : 'danger'} onClose={() => setInviteStatus(null)} dismissible>
                {inviteStatus.message}
              </Alert>
            )}

            {invitedCredentials && (
              <Alert variant="info" className="border">
                <div className="fw-bold mb-2">
                  <i className="bi bi-key-fill me-2"></i>
                  Temporary login for {invitedCredentials.email}
                </div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <code className="p-2 bg-white border rounded flex-grow-1" style={{ wordBreak: 'break-all' }}>
                    {invitedCredentials.password}
                  </code>
                  <Button
                    size="sm"
                    variant={copiedCreds ? 'success' : 'outline-primary'}
                    onClick={() => {
                      navigator.clipboard?.writeText(invitedCredentials.password);
                      setCopiedCreds(true);
                    }}
                  >
                    <i className={`bi ${copiedCreds ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>
                    {copiedCreds ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <small className="text-muted d-block">
                  Share this securely (it was also emailed to them). Shown once — if lost,
                  use <strong>Reset password</strong> in User Management to set a new one.
                </small>
                <Button variant="link" size="sm" className="p-0 mt-1" onClick={() => setInvitedCredentials(null)}>
                  Dismiss
                </Button>
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>User Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={inviting}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Permissions</Form.Label>
              <Form.Select
                value={invitePermissions}
                onChange={(e) => setInvitePermissions(e.target.value as any)}
                disabled={inviting}
              >
                <option value="read">Read Only</option>
                <option value="write">Write Only</option>
                <option value="readwrite">Read & Write</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowInviteModal(false)} disabled={inviting}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleInviteUser}
              disabled={inviting || !inviteEmail}
            >
              {inviting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Sending...
                </>
              ) : (
                <>
                  <i className="bi bi-send me-2"></i>
                  Send Invite
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  };

export default ContainerList; 